import { Box, List, ListItem, ListItemButton, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getProject } from "../services/projectService";
import { IResponseProjectData } from "@types";
import { t } from "i18next";
import { initialProjectData } from "./ProjectPage";
import { AllInboxIcon, SettingsIcon, KeyboardIcon, FolderIcon, MoreHorizIcon } from "../icons";
import { ContentBoxStyle } from "../styles/GlobalStyles";
import { ProjectContent } from "../components/setting/ProjectContent";
import { KeyMappingContent } from "../components/setting/KeyMappingContent";
import { ClassContent } from "../components/setting/ClassContent";
import DatasetSetting from "../components/setting/DatasetSetting";
import { KeyMappingPanel } from "../components/keyboard/KeyMappingPanel";
interface ListItemType {
  icon: React.ReactNode;
  menu: string;
  id?: string; // 튜토리얼 등에서 특정 메뉴를 가리킬 때 사용
}

interface DrawerProps {
  title?: string;
  listItems: ListItemType[];
  selectedItem: string;
  setSelectedItem: React.Dispatch<React.SetStateAction<string>>;
}

export const Drawer = ({ title, listItems, selectedItem, setSelectedItem }: DrawerProps) => {
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: "var(--dk-surface, #fff)",
        borderRight: "1px solid var(--dk-border, #e5e7eb)",
      }}
    >
      {title && (
        <Box
          sx={{
            px: "14px",
            py: "12px",
            borderBottom: "1px solid var(--dk-border, #f3f4f6)",
          }}
        >
          <Typography
            sx={{
              fontSize: "10px",
              fontWeight: 600,
              color: "var(--dk-text-3, #9ca3af)",
              textTransform: "uppercase",
              letterSpacing: "0.07em",
            }}
          >
            {title}
          </Typography>
        </Box>
      )}
      <Box sx={{ flex: 1, p: "8px", display: "flex", flexDirection: "column", gap: "2px" }}>
        {listItems.map((listItem, index) => {
          const isSelected = selectedItem === listItem.menu;
          return (
            <Box
              key={index}
              id={listItem.id}
              onClick={() => setSelectedItem(listItem.menu)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                px: "10px",
                py: "8px",
                borderRadius: "8px",
                cursor: "pointer",
                transition: "all .12s",
                background: isSelected ? "#eff6ff" : "transparent",
                border: isSelected ? "1px solid #bfdbfe" : "1px solid transparent",
                color: isSelected ? "#3b82f6" : "#6b7280",
                "&:hover": {
                  background: isSelected ? "#eff6ff" : "#f9fafb",
                  color: isSelected ? "#3b82f6" : "#374151",
                },
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "7px",
                  flexShrink: 0,
                  background: isSelected ? "#dbeafe" : "#f3f4f6",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all .12s",
                  "& svg": { fontSize: 15, color: isSelected ? "#3b82f6" : "#9ca3af" },
                }}
              >
                {listItem.icon}
              </Box>
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: isSelected ? 600 : 400,
                  color: "inherit",
                  lineHeight: 1,
                }}
              >
                {t(listItem.menu)}
              </Typography>
              {isSelected && (
                <Box
                  sx={{
                    ml: "auto",
                    width: 4,
                    height: 4,
                    borderRadius: "50%",
                    background: "#3b82f6",
                    flexShrink: 0,
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default function ProjectSettingPage() {
  const { id } = useParams();

  const listItems = [
    { icon: <SettingsIcon />, menu: t("General") },
    { icon: <AllInboxIcon />, menu: t("Dataset") },
    { icon: <KeyboardIcon />, menu: t("KeyMapping") },
    { icon: <FolderIcon />, menu: t("Class") },
    { icon: <MoreHorizIcon />, menu: t("Others") },
  ];
  const [selectedItem, setSelectedItem] = useState(t("General"));
  const [projectData, setProjectData] = useState<IResponseProjectData>(initialProjectData);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [labelList, setLabelList] = useState<string[]>([]);

  useEffect(() => {
    const getData = async () => {
      if (id) {
        setIsLoading(true);
        try {
          const response = await getProject(Number(id));
          setProjectData(response.project);
          setLabelList(response.project.class);
        } catch (error) {
          console.error(error);
        } finally {
          setIsLoading(false);
        }
      }
    };
    getData();
  }, []);

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex" }}>
        <Box sx={{ width: "200px" }}>
          <Drawer listItems={listItems} selectedItem={selectedItem} setSelectedItem={setSelectedItem} />
        </Box>
        <Box sx={{ marginLeft: "16px", pt: 2, width: "1000px" }}>
          {!isLoading && (
            <>
              {selectedItem === "General" && (
                <Box sx={ContentBoxStyle}>
                  <ProjectContent id={id} setProjectData={setProjectData} />
                </Box>
              )}
              {selectedItem === "Dataset" && <DatasetSetting id={id} projectData={projectData} />}
              {selectedItem === t("KeyMapping") && (
                <Box>
                  <KeyMappingPanel />
                </Box>
              )}
              {selectedItem === "Class" && (
                <Box sx={ContentBoxStyle}>
                  <ClassContent labelList={labelList} setLabelList={setLabelList} />
                </Box>
              )}
              {selectedItem === "Others" && <EtcContent />}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}

function EtcContent() {
  return <>etc 매핑</>;
}
